import * as Application from 'expo-application'
import Constants from 'expo-constants'
import { useLocalSearchParams, useNavigation, useRouter } from 'expo-router'
import * as Updates from 'expo-updates'
import {
    Briefcase,
    Building2,
    CreditCard,
    LogOut,
    type LucideIcon,
    Shield,
    Tag,
    User,
    UserCircle,
    Users,
} from 'lucide-react-native'
import { Fragment, useEffect } from 'react'
import { Platform, StyleSheet, View } from 'react-native'
import { Divider, List, Text, useTheme } from 'react-native-paper'
import { useCurrentOrg } from '@/hooks/useCurrentOrg'
import { useAuth } from '@/lib/auth'
import { Button } from '@/ui/Button'
import { Card } from '@/ui/Card'
import { Page } from '@/ui/Page'

interface SettingsItem {
    title: string
    description: string
    icon: LucideIcon
    route: string
    access?: 'admin' | 'notWorkforce'
}

const SETTINGS_ITEMS: SettingsItem[] = [
    {
        title: 'Jobs',
        description: 'Create and edit jobs',
        icon: Briefcase,
        route: 'jobs',
        access: 'notWorkforce',
    },
    {
        title: 'Team',
        description: 'Invite members and configure roles',
        icon: Users,
        route: 'team',
        access: 'notWorkforce',
    },
    {
        title: 'Customers',
        description: 'View and edit customer details',
        icon: UserCircle,
        route: 'customers',
        access: 'notWorkforce',
    },
    {
        title: 'Tags',
        description: 'Organize content with tags',
        icon: Tag,
        route: 'tags',
        access: 'notWorkforce',
    },
    {
        title: 'Billing',
        description: 'View subscription and usage',
        icon: CreditCard,
        route: 'billing',
        access: 'admin',
    },
    {
        title: 'Organization Settings',
        description: 'Configure organization settings and preferences',
        icon: Building2,
        route: 'organization',
        access: 'notWorkforce',
    },
    {
        title: 'My Details',
        description: 'Update your profile and password',
        icon: User,
        route: 'my-details',
    },
]

export default function SettingsIndexPage() {
    const params = useLocalSearchParams<{ orgSlug: string }>()
    const navigation = useNavigation()
    const router = useRouter()
    const theme = useTheme()
    const { logout, user } = useAuth({ throwIfAnon: false })
    const { role } = useCurrentOrg({ throwIfNotFound: false })

    const isAdmin = role === 'admin'
    const isNotWorkforce = role !== 'workforce'

    useEffect(() => {
        if (role !== 'workforce' || Platform.OS === 'ios') return
        navigation.setOptions({
            headerLeft: () => (
                <Button
                    title="Time Entry"
                    icon="chevron-left"
                    onPress={() => router.replace(`/${params.orgSlug}/entry`)}
                />
            ),
        })
    }, [role, navigation, router, params.orgSlug])
    const isSuperUser = user?.metadata?.isSuperUser === true

    const handleLogout = () => {
        logout()
        router.replace('/auth/sign-in')
    }

    const visibleItems = SETTINGS_ITEMS.filter(item => {
        if (!item.access) return true
        if (item.access === 'admin') return isAdmin
        return isNotWorkforce
    })

    if (isSuperUser) {
        visibleItems.push({
            title: 'Superuser Tools',
            description: 'Impersonate users for debugging',
            icon: Shield,
            route: 'superuser',
        })
    }

    return (
        <Page>
            <Card>
                <Card.Content>
                    <Text variant="titleLarge" style={styles.sectionTitle}>
                        Settings
                    </Text>

                    <Divider style={styles.divider} />

                    {visibleItems.map((item, index) => {
                        const Icon = item.icon
                        return (
                            <Fragment key={item.route}>
                                {index > 0 && <Divider />}
                                <List.Item
                                    title={item.title}
                                    description={item.description}
                                    // E2E: gotoScreen() clicks this row by testID. Do not remove.
                                    testID={`nav-settings-${item.route}`}
                                    left={props => (
                                        <List.Icon
                                            {...props}
                                            icon={() => (
                                                <Icon size={24} color={theme.colors.onSurface} />
                                            )}
                                        />
                                    )}
                                    right={props => <List.Icon {...props} icon="chevron-right" />}
                                    onPress={() =>
                                        router.push(`/${params.orgSlug}/settings/${item.route}`)
                                    }
                                    style={styles.listItem}
                                />
                            </Fragment>
                        )
                    })}
                </Card.Content>
            </Card>

            <Card style={styles.signOutCard}>
                <Card.Content>
                    <View style={styles.signOutSection}>
                        <View style={styles.signOutHeader}>
                            <LogOut size={18} color={theme.colors.onSurfaceVariant} />
                            <Text variant="titleMedium" style={styles.subsectionTitle}>
                                Sign Out
                            </Text>
                        </View>
                        <Text
                            variant="bodyMedium"
                            style={[
                                styles.signOutDescription,
                                { color: theme.colors.onSurfaceVariant },
                            ]}
                        >
                            Sign out of your account on this device.
                        </Text>
                        <Button
                            title="Sign Out"
                            mode="outlined"
                            onPress={handleLogout}
                            icon="logout"
                            style={styles.signOutButton}
                        />
                    </View>
                </Card.Content>
            </Card>

            <Text style={[styles.versionText, { color: theme.colors.onSurfaceVariant }]}>
                Version {Constants.expoConfig?.version} (
                {Application.nativeBuildVersion || 'unknown'})
                {user?.metadata?.isBetaTester ? ' (beta tester)' : ''}
                {'\n'}
                {Updates.updateId
                    ? `Update Id: ${Updates.updateId.slice(0, 8)}`
                    : 'Embedded bundle'}
                {process.env.EXPO_PUBLIC_GIT_SHA
                    ? ` · git ${process.env.EXPO_PUBLIC_GIT_SHA.slice(0, 7)}`
                    : ''}
            </Text>
        </Page>
    )
}

const styles = StyleSheet.create({
    sectionTitle: {
        fontWeight: '600',
        marginBottom: 8,
    },
    divider: {
        marginVertical: 8,
    },
    listItem: {
        paddingHorizontal: 0,
    },
    signOutCard: {
        marginTop: 24,
        marginBottom: 24,
    },
    signOutSection: {
        gap: 12,
    },
    signOutHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        marginBottom: 4,
    },
    subsectionTitle: {
        fontWeight: '600',
    },
    signOutDescription: {
        marginBottom: 12,
    },
    signOutButton: {
        alignSelf: 'flex-start',
        minWidth: 150,
    },
    signOutButtonContent: {
        paddingHorizontal: 8,
    },
    versionText: {
        textAlign: 'center',
        marginBottom: 24,
    },
})
